import { jwtDecode } from 'jwt-decode'
import type { StytchJWTPayload } from '@/types/stytch'
import { fetchUserIdentityFromJwtDev } from '@/repositories/devIdentityRepository'
import { isLocalPortalApiBaseUrlValue } from '@/utilities/api/portalApiBaseUrl'
import { getPostSignupAuthUserIdBridge } from '@/utilities/auth/postSignupAuthUserIdBridge'

export type StytchAuthInfo = {
  memberId: string | null
  authUserId: string | null
}

export type HeaderSetter = {
  set(name: string, value: string): unknown
  has?(name: string): boolean
}

/**
 * Extracts authentication provider headers and auth user ID from the Stytch session JWT cookie.
 *
 * The portal-api requires x-auth-provider and x-auth-provider-record-id headers
 * for certain endpoints (e.g., /v3/auth/client/sign-up). These headers identify
 * which authentication provider was used (Stytch) and the user's member ID.
 * The auth user ID (internal_id from JWT custom claims) is used for x-auth-user-id.
 *
 * In the old client-portal-ui, these headers were automatically injected by an
 * auth-proxy sidecar container in Kubernetes. Since the new portals app runs on
 * Vercel without that infrastructure, we extract them from the Stytch session
 * cookie and inject them via an axios interceptor.
 *
 * @returns Object with x-auth-provider, x-auth-provider-record-id, and authUserId, or null if not available
 */

export function decodeStytchAuthFromJwt(jwt: string | null): StytchAuthInfo {
  if (!jwt) return { memberId: null, authUserId: null }
  try {
    const decoded = jwtDecode<StytchJWTPayload>(jwt)
    const memberId = decoded?.sub ? String(decoded.sub) : null
    const authUserId = decoded?.internal_id == null ? null : String(decoded.internal_id)
    return { memberId, authUserId }
  } catch {
    return { memberId: null, authUserId: null }
  }
}

export function resolveStytchAuthForRequest(args: {
  jwt: string | null
  authUserIdOverride: string | null
}): StytchAuthInfo {
  const auth = decodeStytchAuthFromJwt(args.jwt)
  const postSignupAuthUserId = getPostSignupAuthUserIdBridge({
    memberId: auth.memberId,
    jwtAuthUserId: auth.authUserId,
  })

  return {
    ...auth,
    authUserId: args.authUserIdOverride ?? auth.authUserId ?? postSignupAuthUserId,
  }
}

export function injectStytchAuthHeaders(
  headers: HeaderSetter | undefined,
  auth: StytchAuthInfo,
): void {
  if (!headers) return
  if (!auth.memberId) return

  headers.set('x-auth-provider', 'stytch')
  headers.set('x-auth-provider-record-id', auth.memberId)
  if (auth.authUserId) headers.set('x-auth-user-id', auth.authUserId)
}

export type IdentityCacheValue = { authUserId: string; identity: string } | null

export type IdentityCache = {
  get(): IdentityCacheValue
  set(value: IdentityCacheValue): void
}

export async function injectLocalUserIdentity(args: {
  baseUrl: string
  headers: HeaderSetter
  jwt: string | null
  authUserId: string | null
  cache?: IdentityCache
}): Promise<void> {
  const { baseUrl, headers, jwt, authUserId, cache } = args
  if (!isLocalPortalApiBaseUrlValue(baseUrl)) return

  if (!jwt) {
    cache?.set(null)
    return
  }

  const authUserIdKey = authUserId ?? ''

  const existing = cache?.get() ?? null

  if (existing?.authUserId === authUserIdKey) {
    headers.set('x-user-identity', existing.identity)
    return
  }

  const identityJson = await fetchUserIdentityFromJwtDev({
    baseUrl,
    jwt,
    authUserId,
  })
  if (!identityJson) {
    cache?.set(null)
    return
  }

  cache?.set({ authUserId: authUserIdKey, identity: identityJson })
  headers.set('x-user-identity', identityJson)
}
